import { FunctionComponent } from "react";
import { SecondarySingleSelectDropdown } from "@coherehealth/common";
// eslint-disable-next-line cohere-react/no-mui-styled-import
import { styled } from "@material-ui/core/styles";
import { useTheme, useMediaQuery } from "@material-ui/core";
import { Tooltip } from "@mui/material";

export type DateFilterOptions = "1" | "7" | "14" | "28";
export interface ChartItem {
  name: string;
  count: number;
  aggregations?: Record<string, number>;
}
export type ChartData = ChartItem[];

interface DateFilterOption {
  id: DateFilterOptions;
  label: string;
}

export const defaultSkill = { id: "All skills", label: "All skills" };

export const CASE_DUE_DATE_DATE_FILTER_OPTIONS: DateFilterOption[] = [
  { id: "7", label: "Next 7 days" },
  { id: "14", label: "Next 14 days" },
  { id: "28", label: "Next 28 days" },
];

export const CASE__HISTORICAL_DUE_DATE_DATE_FILTER_OPTIONS: DateFilterOption[] = [
  { id: "7", label: "Last 7 days" },
  { id: "14", label: "Last 14 days" },
  { id: "28", label: "Last 28 days" },
];

export const CASE_HISTORICAL_PRODUCTION_DATE_FILTER_OPTIONS: DateFilterOption[] = [
  { id: "1", label: "Today" },
  ...CASE__HISTORICAL_DUE_DATE_DATE_FILTER_OPTIONS,
];

interface VisualizationDateFilterProps {
  options: DateFilterOption[];
  dateValue: string;
  setDateValue: (dateValue: DateFilterOptions, skill?: string) => void;
  historical?: boolean;
}

export const VisualizationDateFilter = ({ options, dateValue, setDateValue }: VisualizationDateFilterProps) => {
  return (
    <SecondarySingleSelectDropdown
      options={options}
      value={dateValue}
      onChange={(val) => setDateValue(val as DateFilterOptions)}
      label="Filter By:"
      style={{ width: 150 }}
    />
  );
};

type TickPayload = {
  coordinate: number;
  index: number;
  isShow: boolean;
  offset: number;
  tickCoord: number;
  value: string;
};

interface CustomAxisTickProps {
  x: number;
  y: number;
  payload: TickPayload;
  hourly?: boolean;
}

export const CustomizedAxisTick = ({ x, y, payload, hourly }: CustomAxisTickProps) => {
  const datePayload = new Date(payload.value);
  const month = datePayload.getMonth() + 1;
  const date = datePayload.getDate();
  const day = typeof payload.value === "string" ? payload.value?.slice(0, 3) : "";
  const hours = typeof payload.value === "string" ? Number(payload.value.slice(0, 2)) : 0;
  const hourlyLabel = hours - 12 > 0 ? hours - 12 : hours;
  const timeAddendum = hours >= 12 ? "pm" : "am";
  return (
    <g transform={`translate(${x},${y})`}>
      <Body2Text x={0} y={0} dy={10} textAnchor="middle" fill="#666">
        {hourly ? `${hourlyLabel}:00` : `${month}/${date}`}
      </Body2Text>
      <Body2Text x={0} y={0} dy={25} textAnchor="middle" fill="#666">
        {hourly ? timeAddendum : day}
      </Body2Text>
    </g>
  );
};

interface CustomAxisTickProps {
  x: number;
  y: number;
  value: string;
}

export const CustomizedBarLabel = ({ x, y, value }: CustomAxisTickProps) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <Body2Text x={0} y={-5} dy={0} textAnchor="start" fill="#666">
        {value}
      </Body2Text>
    </g>
  );
};

export const RoundedCustomizedBarLabel = ({ x, y, value }: CustomAxisTickProps) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <Body2Text x={0} y={-5} dy={0} textAnchor="start" fill="#666">
        {+(+value).toFixed(1)}
      </Body2Text>
    </g>
  );
};

// eslint-disable-next-line cohere-react/no-mui-styled-import
const Body2Text = styled("text")(() => ({
  fontFamily: "Figtree, sans-serif",
  fontWeight: 600,
  fontSize: "15px",
  lineHeight: "19px",
  letterSpacing: "0px",
}));

export const usePageResponsiveGridItemSize = () => {
  const theme = useTheme();
  const eightColumns = useMediaQuery(theme.breakpoints.up("xl"));
  const sixColumns = useMediaQuery(theme.breakpoints.up("lg"));
  const fourCoumns = useMediaQuery(theme.breakpoints.up("md"));

  return eightColumns ? 1.5 : sixColumns ? 2 : fourCoumns ? 3 : 4;
};

export const CustomTooltip: FunctionComponent<
  React.PropsWithChildren<{
    active?: boolean;
    payload?: any;
    Content: FunctionComponent<React.PropsWithChildren<{ payload: any }>>;
  }>
> = ({ active, payload, Content }) => {
  if (active && payload && payload.length) {
    return (
      <Tooltip
        open={active}
        title={<Content payload={payload} />}
        placement="top-start"
        arrow
        PopperProps={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 15],
              },
            },
          ],
          sx: {
            "& .MuiTooltip-tooltip": {
              backgroundColor: "#2B3648",
              color: "#FFFFFF",
              width: "200px",
              height: "100px",
              boxShadow: "1px 3px 2px rgba(207, 226, 231, 0.8)",
            },
            "& .MuiTooltip-arrow": {
              color: "#2B3648",
            },
          },
        }}
      >
        <span style={{ visibility: "hidden" }}>h</span>
      </Tooltip>
    );
  }
  return null;
};
